// src/utils/quizImport.js
//
// Quiz-Fragen aus einer Tabelle (Excel/Google Sheets, als CSV gespeichert
// oder direkt eingefuegt). Die Spalten werden an der Ueberschrift erkannt:
//
//   Frage / Behauptung / Aussage      -> Fragetext
//   Richtig / Lösung / Korrekt        -> richtige Antwort
//   Antwort A … D (oder nur A … D)    -> Antworten bei Mehrfachauswahl
//
// Weitere Spalten (Nr., Themengebiet, …) werden ignoriert. Fehlen erkennbare
// Ueberschriften, gilt die Reihenfolge des Exports:
//
//   Frage ; Richtig ; Antwort A ; Antwort B ; Antwort C ; Antwort D
//
//  - Wahr/Falsch: Antwortspalten leer lassen, "Richtig" = wahr oder falsch
//    (auch w/f, ja/nein, true/false).
//  - Mehrfachauswahl: 3 oder 4 Antworten, "Richtig" = Buchstabe (A–D),
//    Nummer (1–4) oder der Antworttext selbst.
//
// Die endgueltige Pruefung macht der Server (services/quiz.js); hier geht es
// um verstaendliche Meldungen mit Zeilennummer, bevor etwas gespeichert wird.

import { parseCSV } from "./parseTabular";

const WAHR = ["wahr", "w", "ja", "j", "true", "richtig", "stimmt"];
const FALSCH = ["falsch", "f", "nein", "n", "false", "stimmt nicht"];

function richtigeAntwort(roh, antworten) {
  const wert = String(roh || "").trim();
  if (!wert) return -1;
  const buchstabe = "ABCD".indexOf(wert.toUpperCase());
  if (wert.length === 1 && buchstabe >= 0 && buchstabe < antworten.length) return buchstabe;
  if (/^\d$/.test(wert)) {
    const n = Number(wert) - 1;
    return n >= 0 && n < antworten.length ? n : -1;
  }
  return antworten.findIndex((a) => a.toLowerCase() === wert.toLowerCase());
}

const norm = (h) => String(h || "").trim().toLowerCase();
const IST_FRAGE = /^(frage|fragetext|behauptung|aussage)/;
const IST_RICHTIG = /^(richtig|richtige antwort|lösung|loesung|korrekt|antwort richtig)/;
const IST_ANTWORT = /^(?:antwort\s*)?([a-d])$/;

/**
 * Spaltenindizes { frage, richtig, antworten[] } aus den Ueberschriften –
 * oder die feste Export-Reihenfolge, wenn Frage/Richtig nicht erkennbar sind.
 */
export function spaltenErkennen(headers) {
  const h = headers.map(norm);
  const frage = h.findIndex((x) => IST_FRAGE.test(x));
  const richtig = h.findIndex((x) => IST_RICHTIG.test(x));
  if (frage < 0 || richtig < 0) return { frage: 0, richtig: 1, antworten: [2, 3, 4, 5], erkannt: false };
  const antworten = h
    .map((x, i) => ({ i, m: x.match(IST_ANTWORT) }))
    .filter((a) => a.m)
    .sort((a, b) => a.m[1].localeCompare(b.m[1]))
    .map((a) => a.i);
  return { frage, richtig, antworten, erkannt: true };
}

/**
 * Text -> { fragen, fehler }. `fehler` ist eine Liste von Meldungen; Zeilen
 * mit Fehler fehlen in `fragen`.
 */
export function fragenAusTabelle(text) {
  const { headers, rows } = parseCSV(text);
  const fragen = [];
  const fehler = [];
  if (headers.length < 2) {
    return { fragen, fehler: ["Mindestens zwei Spalten nötig: Frage und Lösung (erste Zeile = Überschriften)."] };
  }

  const spalten = spaltenErkennen(headers);
  rows.forEach((row, i) => {
    const zeile = i + 2; // +1 Kopfzeile, +1 menschlich gezaehlt
    const zelle = (k) => (k === undefined ? "" : String(row[headers[k]] ?? "").trim());
    // Zeilenumbrueche in Excel-Zellen (lange Behauptungen) zu Leerzeichen.
    const frageText = zelle(spalten.frage).replace(/\s*\n\s*/g, " ");
    const richtig = zelle(spalten.richtig);
    const antworten = spalten.antworten.map(zelle).filter(Boolean);
    if (!frageText) {
      fehler.push(`Zeile ${zeile}: Fragetext fehlt.`);
      return;
    }

    if (antworten.length === 0) {
      const r = richtig.toLowerCase();
      if (WAHR.includes(r)) fragen.push({ typ: "wf", text: frageText, antworten: ["Wahr", "Falsch"], richtig: 0 });
      else if (FALSCH.includes(r)) fragen.push({ typ: "wf", text: frageText, antworten: ["Wahr", "Falsch"], richtig: 1 });
      else fehler.push(`Zeile ${zeile}: „${richtig}“ ist weder wahr noch falsch.`);
      return;
    }

    if (antworten.length < 3) {
      fehler.push(`Zeile ${zeile}: Mehrfachauswahl braucht 3 oder 4 Antworten (oder keine für Wahr/Falsch).`);
      return;
    }
    const idx = richtigeAntwort(richtig, antworten);
    if (idx < 0) {
      fehler.push(`Zeile ${zeile}: richtige Antwort „${richtig}“ nicht erkannt (A–D, 1–4 oder der Antworttext).`);
      return;
    }
    fragen.push({ typ: "mc", text: frageText, antworten, richtig: idx });
  });

  return { fragen, fehler };
}

/** Fragen als CSV (Semikolon, fuer Excel) – Sicherung und Vorlage zugleich. */
export function fragenAlsCsv(fragen) {
  const feld = (t) => {
    const s = String(t ?? "");
    return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const zeilen = [["Frage", "Richtig", "Antwort A", "Antwort B", "Antwort C", "Antwort D"]];
  for (const f of fragen) {
    if (f.typ === "wf") zeilen.push([f.text, f.richtig === 0 ? "wahr" : "falsch", "", "", "", ""]);
    else zeilen.push([f.text, "ABCD"[f.richtig], ...f.antworten, ...Array(4 - f.antworten.length).fill("")]);
  }
  return zeilen.map((z) => z.map(feld).join(";")).join("\r\n");
}
